// ── Slack Apps Manifest API ──────────────────────────────────────────────────
// Creates and deletes Slack apps programmatically via the `apps.manifest.*`
// REST API. Requires a short-lived "app configuration token" obtained from
// https://api.slack.com/apps → Generate Token.

import type { SlackAppManifest } from '../types/channel-config.js';

const SLACK_MANIFEST_CREATE_URL = 'https://slack.com/api/apps.manifest.create';
const SLACK_MANIFEST_DELETE_URL = 'https://slack.com/api/apps.manifest.delete';
const SLACK_MANIFEST_EXPORT_URL = 'https://slack.com/api/apps.manifest.export';
const SLACK_MANIFEST_UPDATE_URL = 'https://slack.com/api/apps.manifest.update';
const SLACK_TOKENS_ROTATE_URL = 'https://slack.com/api/tooling.tokens.rotate';
// ── Token rotation ──────────────────────────────────────────────────────────

export interface SlackTokenRotateResult {
  token: string;
  refresh_token: string;
  exp: number;
  iat: number;
}

/**
 * Rotate a Slack configuration access token using a refresh token.
 *
 * @param clientId - The app's client_id (from apps.manifest.create response).
 * @param clientSecret - The app's client_secret.
 * @param refreshToken - The refresh token from the previous rotation (or initial generation).
 * @returns A fresh config token and new refresh token.
 */
export async function rotateSlackConfigToken(
  clientId: string,
  clientSecret: string,
  refreshToken: string,
): Promise<SlackTokenRotateResult> {
  const body = new URLSearchParams();
  body.set('client_id', clientId);
  body.set('client_secret', clientSecret);
  body.set('refresh_token', refreshToken);
  body.set('grant_type', 'refresh_token');

  const response = await fetch(SLACK_TOKENS_ROTATE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  const data = (await response.json()) as {
    ok: boolean;
    error?: string;
    token?: string;
    refresh_token?: string;
    exp?: number;
    iat?: number;
  };

  if (!data.ok || !data.token || !data.refresh_token) {
    throw new SlackApiError(
      `Config token rotation failed: ${data.error ?? 'unknown_error'}`,
      data.error,
    );
  }

  return {
    token: data.token,
    refresh_token: data.refresh_token,
    exp: data.exp ?? 0,
    iat: data.iat ?? 0,
  };
}

// ── Manifest export & update ────────────────────────────────────────────────

/**
 * Export (read) the current app manifest from Slack.
 *
 * @param configToken - A fresh configuration access token.
 * @param appId - The Slack app ID.
 * @returns The current manifest as configured in Slack.
 */
export async function exportSlackManifest(
  configToken: string,
  appId: string,
): Promise<SlackAppManifest> {
  const body = new URLSearchParams();
  body.set('token', configToken);
  body.set('app_id', appId);

  const response = await fetch(SLACK_MANIFEST_EXPORT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  const data = (await response.json()) as {
    ok: boolean;
    error?: string;
    manifest?: SlackAppManifest;
  };

  if (!data.ok || !data.manifest) {
    throw new SlackApiError(
      `Manifest export failed: ${data.error ?? 'unknown_error'}`,
      data.error,
    );
  }

  return data.manifest;
}

/**
 * Update an existing Slack app's manifest.
 *
 * @param configToken - A fresh configuration access token.
 * @param appId - The Slack app ID.
 * @param manifest - The new manifest to apply.
 */
export async function updateSlackManifest(
  configToken: string,
  appId: string,
  manifest: SlackAppManifest,
): Promise<void> {
  const body = new URLSearchParams();
  const manifestWithMeta = { _metadata: { major_version: 2 }, ...manifest };

  body.set('token', configToken);
  body.set('app_id', appId);
  body.set('manifest', JSON.stringify(manifestWithMeta));

  const response = await fetch(SLACK_MANIFEST_UPDATE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  const data = (await response.json()) as {
    ok: boolean;
    error?: string;
  };

  if (!data.ok) {
    throw new SlackApiError(
      `Manifest update failed: ${data.error ?? 'unknown_error'}`,
      data.error,
    );
  }
}

// ── App creation & deletion ─────────────────────────────────────────────────

export interface SlackCreateAppCredentials {
  client_id: string;
  client_secret: string;
  verification_token: string;
  signing_secret: string;
}

export interface SlackCreateAppResult {
  app_id: string;
  credentials: SlackCreateAppCredentials;
  oauth_authorize_url: string;
}

export class SlackApiError extends Error {
  constructor(
    message: string,
    public readonly slackError?: string,
  ) {
    super(message);
    this.name = 'SlackApiError';
  }
}

/**
 * Create a Slack app via the `apps.manifest.create` API.
 *
 * @param configToken - A Slack app configuration token (starts with `xoxe-`).
 *   Obtain one from https://api.slack.com/apps → "Generate Token".
 *   These tokens are per-user, per-workspace, and last 12 hours.
 * @param manifest - The Slack app manifest object (as generated by `generateSlackAppManifest`).
 * @returns The created app's ID, credentials, and OAuth URL.
 * @throws {SlackApiError} if the Slack API returns an error.
 */
export async function createSlackApp(
  configToken: string,
  manifest: SlackAppManifest,
): Promise<SlackCreateAppResult> {
  const manifestWithMeta = { _metadata: { major_version: 2 }, ...manifest };

  const body = new URLSearchParams();
  body.set('token', configToken);
  body.set('manifest', JSON.stringify(manifestWithMeta));

  const response = await fetch(SLACK_MANIFEST_CREATE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new SlackApiError(
      `Slack API returned HTTP ${response.status}: ${response.statusText}`,
    );
  }

  const data = (await response.json()) as {
    ok: boolean;
    error?: string;
    errors?: unknown[];
    response_metadata?: { messages?: string[] };
    app_id?: string;
    credentials?: {
      client_id: string;
      client_secret: string;
      verification_token: string;
      signing_secret: string;
    };
    oauth_authorize_url?: string;
  };

  if (!data.ok) {
    const details = data.errors
      ? ` — details: ${JSON.stringify(data.errors)}`
      : data.response_metadata?.messages
        ? ` — ${data.response_metadata.messages.join('; ')}`
        : '';
    console.error('[slack-api] createSlackApp failed:', JSON.stringify(data, null, 2));
    throw new SlackApiError(
      `Slack API error: ${data.error ?? 'unknown_error'}${details}`,
      data.error,
    );
  }

  if (!data.app_id || !data.credentials || !data.oauth_authorize_url) {
    throw new SlackApiError('Slack API returned incomplete response');
  }

  return {
    app_id: data.app_id,
    credentials: data.credentials,
    oauth_authorize_url: data.oauth_authorize_url,
  };
}

/**
 * Delete a Slack app via the `apps.manifest.delete` API.
 *
 * @param configToken - A Slack app configuration token (starts with `xoxe-`).
 * @param appId - The Slack app ID to delete.
 * @throws {SlackApiError} if the Slack API returns an error.
 */
export async function deleteSlackApp(
  configToken: string,
  appId: string,
): Promise<void> {
  const body = new URLSearchParams();
  body.set('token', configToken);
  body.set('app_id', appId);

  const response = await fetch(SLACK_MANIFEST_DELETE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new SlackApiError(
      `Slack API returned HTTP ${response.status}: ${response.statusText}`,
    );
  }

  const data = (await response.json()) as {
    ok: boolean;
    error?: string;
  };

  if (!data.ok) {
    throw new SlackApiError(
      `Slack API error: ${data.error ?? 'unknown_error'}`,
      data.error,
    );
  }
}
